import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, MenuItem, Select, FormControl, InputLabel, Grid, Alert, IconButton
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import BSDateInput from '../common/BSDateInput';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];
const unitOptions = ['Pcs', 'Pair', 'Set'];

const DirectOrder = () => {
  const [karigar, setKarigar] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [salesman, setSalesman] = useState('');
  const [order, setOrder] = useState({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '', isUrgent: false });
  const [items, setItems] = useState([
    { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }
  ]);
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [directOrderId, setDirectOrderId] = useState(null);
  const [shouldFetchDirectOrder, setShouldFetchDirectOrder] = useState(false);
  const lastAttemptedBillNumber = useRef('');
  const [directOrderExists, setDirectOrderExists] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchKarigars = async () => {
      try {
        const res = await api.get('/api/karigar');
        setKarigars(res.data);
      } catch {}
    };
    const fetchItems = async () => {
      try {
        const res = await api.get('/api/items');
        setItemOptions(res.data.map(item => item.name));
      } catch {}
    };
    fetchKarigars();
    fetchItems();
    // Only pre-fill from localStorage if billNumber is empty
    setOrder(o => {
      if (!o.billNumber) {
        const storedBillNumber = localStorage.getItem('sharedBillNumber');
        return {
          ...o,
          billNumber: storedBillNumber || ''
        };
      }
      return o;
    });
    // If you want to add billName, do similar for billName
  }, []);

  // Only fetch when shouldFetchDirectOrder is true and billNumber is filled
  useEffect(() => {
    if (shouldFetchDirectOrder && order.billNumber) {
      api.get(`/api/directorders?billNumber=${encodeURIComponent(order.billNumber)}`)
        .then(res => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setDirectOrderExists(true);
            // Optionally, show a warning here
          } else {
            setDirectOrderExists(false);
            setOrder({
              billNumber: lastAttemptedBillNumber.current,
              orderDate: '',
              deliveryDate: '',
              requiredDate: '',
              isUrgent: false,
              status: 'Pending',
              agentName: '',
              karigarAbbreviation: '',
              totalAmount: 0
            });
            setItems([{ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
          }
        })
        .catch(() => { setDirectOrderExists(false); });
      setShouldFetchDirectOrder(false);
    }
  }, [shouldFetchDirectOrder, order.billNumber]);

  const handleKarigarChange = e => setKarigar(e.target.value);
  const handleSalesmanChange = e => setSalesman(e.target.value);
  const handleOrderChange = e => {
    const { name, value, type, checked } = e.target;
    setOrder(o => ({ ...o, [name]: type === 'checkbox' ? checked : value }));
    // Synchronize billNumber and billName to localStorage
    if (name === 'billNumber') localStorage.setItem('sharedBillNumber', value);
    // Clear directOrderId if billNumber changes
    if (name === 'billNumber') setDirectOrderId(null);
    // If you want to add billName, do similar for billName
  };
  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setItems(items => items.map((item, i) => i === idx ? { ...item, [name]: value } : item));
  };
  const addItem = () => setItems(items => [...items, { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
  const removeItem = idx => setItems(items => items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const validate = () => {
    if (!karigar || !karigar.trim()) return 'Karigar is required.';
    if (!salesman || !salesman.trim()) return 'Salesman is required.';
    if (!order.billNumber || !order.billNumber.trim() || !order.orderDate || !order.deliveryDate || !order.requiredDate) return 'All order fields are required.';
    for (const item of items) {
      if (!item.itemName || !item.itemName.trim() || !item.karat || !item.karat.trim() || !item.quantity || !item.unit || !item.unit.trim() || !item.itemWeight) return 'All item fields are required.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }
    if (directOrderExists) {
      setError('This bill already exists. Please use the Update Direct Order form to edit.');
      return;
    }
    setLoading(true);
    try {
      // Calculate totalAmount for each item and for the order
      const computedItems = items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const rate = parseFloat(item.rate) || 0;
        const makingCharge = parseFloat(item.makingCharge) || 0;
        const stoneAmount = parseFloat(item.stoneAmount) || 0;
        const total = weight + wastage;
        const netAmount = weight ? ((weight / 11.664) * rate) : 0;
        const totalAmount = netAmount + makingCharge + stoneAmount;
        return {
          ...item,
          total: total ? total.toFixed(3) : '',
          netAmount: netAmount ? netAmount.toFixed(2) : '',
          totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
        };
      });
      const orderTotalAmount = computedItems.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
      await api.post('/api/directorders', {
        billNumber: order.billNumber,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        requiredDate: order.requiredDate,
        status: 'Pending',
        agentName: salesman,
        karigarAbbreviation: karigar,
        items: computedItems.map(item => ({
          ...item,
          quantity: Number(item.quantity),
          itemWeight: Number(item.itemWeight),
          total: Number(item.total),
          netAmount: Number(item.netAmount),
          totalAmount: Number(item.totalAmount),
          gsb: Number(item.gsb) || 0,
          wastage: Number(item.wastage) || 0,
          rate: Number(item.rate) || 0,
          makingCharge: Number(item.makingCharge) || 0,
          stoneAmount: Number(item.stoneAmount) || 0,
          despatchStatus: true,
          workIssued: false,
          karigarAbbreviation: karigar
        })),
        totalAmount: orderTotalAmount,
        isUrgent: false,
        isActive: true
      });
      setSuccess('Direct order created successfully!');
      setKarigar('');
      setSalesman('');
      setOrder({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '', isUrgent: false, status: 'Pending', agentName: '', karigarAbbreviation: '', totalAmount: 0 });
      setItems([{ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
      localStorage.removeItem('sharedBillNumber');
      localStorage.removeItem('sharedBillName');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create direct order');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/update-direct-order')}>Update Existing Direct Order</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Karigar Direct Order Entry
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {directOrderExists && <Alert severity="warning">This bill already exists. Please use the Update Direct Order form to edit.</Alert>}
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ mt: 2 }}>Order Details</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth required size="small">
                <InputLabel>Karigar Name</InputLabel>
                <Select value={karigar} label="Karigar Name" onChange={handleKarigarChange}>
                  {karigars.map(k => (
                    <MenuItem key={k.abbreviation} value={k.abbreviation}>
                      {k.abbreviation} - {k.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Salesman" name="salesman" value={salesman} onChange={handleSalesmanChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Bill Number" name="billNumber" value={order.billNumber} onChange={handleOrderChange} onBlur={e => { lastAttemptedBillNumber.current = e.target.value; setShouldFetchDirectOrder(true); }} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Order Date"
                value={order.orderDate}
                onChange={(date) => setOrder(o => ({ ...o, orderDate: date }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Delivery Date"
                value={order.deliveryDate}
                onChange={(date) => setOrder(o => ({ ...o, deliveryDate: date }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Required Date"
                value={order.requiredDate}
                onChange={(date) => setOrder(o => ({ ...o, requiredDate: date }))}
                required
              />
            </Grid>
          </Grid>
          <Typography variant="h6" sx={{ mt: 2 }}>Order Items</Typography>
          {items.map((item, idx) => (
            <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Item Name</InputLabel>
                  <Select name="itemName" value={item.itemName} label="Item Name" onChange={e => handleItemChange(idx, e)}>
                    {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Karat</InputLabel>
                  <Select name="karat" value={item.karat} label="Karat" onChange={e => handleItemChange(idx, e)}>
                    {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Quantity" name="quantity" type="number" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select name="unit" value={item.unit} label="Unit" onChange={e => handleItemChange(idx, e)}>
                    {unitOptions.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Item Weight" name="itemWeight" type="number" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}><Remove /></IconButton>
                <IconButton onClick={addItem}><Add /></IconButton>
              </Grid>
            </Grid>
          ))}
          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Order'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default DirectOrder; 